import * as React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import glamorous from 'glamorous';
import { Button, Typography, AccordionDetails, Accordion, AccordionSummary } from '@material-ui/core';
import UnfoldMoreOutlinedIcon from '@material-ui/icons/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@material-ui/icons/UnfoldLessOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { PanelIcon } from '../../Dashboard/Component/Panel';
import { AnimatedItem } from '../../components/animations/AnimatedItem';
import { RvDataTopicInfoCard } from './DataTopicInfoCard';
import { RvDataTopicInfo } from './DataTopicInfo';
import { MATTER_DETAILS_REFRESH_EVENT } from '../MatterDetails';
import EventSystem from '../../EventSystem';

const PanelDetails = glamorous(AccordionDetails) ({
    // minHeight: '440px !important'
});

const CommentCard = glamorous.div({
    width:        '100%',
    background:   '#fafbff',
    border:       '1px solid rgba(81,115,255,0.15)',
    borderRadius: '8px',
    padding:      '12px 16px',
    marginBottom: '10px',
    boxShadow:    '0 1px 3px rgba(0,0,0,0.06)',
    ':last-child': { marginBottom: 0 },
});

// tslint:disable-next-line:no-any
const RelationshipWrapper = glamorous.div<{ theme?: any }>((props) => ({
    paddingBottom: '10px',
    [`& .${`centered`}`] : {
        [`> ${`div`}`] : {
            width: '100%'
        }        
    }    
}));
const PanelTitle = glamorous(Typography) ({
    display: 'flex !important',
    alignItems: 'center !important',
    fontSize: '14px !important',
    gap: '10px',
});

// Same section-header icon chip EnquiryFormDrawer.tsx uses for its own accordion sections
// (28px rounded-square, accent bg/fg) - explicit request for consistency, replacing the old
// office-ui-fabric-react Icon (no chip, no colour) this panel used before.
const PanelIconChip = glamorous.span<{ bg: string; color: string }>((props) => ({
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: props.bg,
    color: props.color,
}));

// Same Expand all/Collapse all toggle button already established in AdminDashboard.tsx and
// Parties.tsx (icon + label pattern) - no page-shell toolbar slot to put it in here either, so
// this is a plain row above the panels, same as Parties.tsx's own ExpandToggleWrapper.
const ExpandToggleWrapper = glamorous.div({
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '8px',
});

interface TopicsInfoProps {
    matterId: number | string;
    sourceSystem?: string;
    data: Data;
}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    matter: {
        // tslint:disable-next-line:no-any
        topics: any;
    };
    refetch: () => void;
}

interface TopicsInfoState {
    expandedIndices: { [index: number]: boolean };
}

export class TopicsInfo extends React.Component<TopicsInfoProps, TopicsInfoState>  {

    constructor(props: TopicsInfoProps) {
        super(props);
        this.state = { expandedIndices: {} };
        this.toggleExpanded = this.toggleExpanded.bind(this);
        this.toggleExpandAll = this.toggleExpandAll.bind(this);
    }

    componentDidMount() {
        EventSystem.on(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    componentWillUnmount() {
        EventSystem.event.removeListener(MATTER_DETAILS_REFRESH_EVENT, this.onRefresh);
    }

    private onRefresh = () => {
        this.props.data.refetch();
    }

    private toggleExpanded(index: number) {
        this.setState(prevState => ({
            expandedIndices: {
                ...prevState.expandedIndices,
                [index]: !prevState.expandedIndices[index],
            },
        }));
    }

    // renderedIndices is the same de-duplicated set of indices the render loop below actually
    // turns into a panel (Actionstep Comments topics past the first are skipped, not their own
    // panel) - passed in rather than recomputed so isAllExpanded/toggleExpandAll never drift
    // out of sync with what render() decides to skip.
    private isAllExpanded(renderedIndices: number[]): boolean {
        if (renderedIndices.length === 0) {
            return false;
        }
        return renderedIndices.every((index) => !!this.state.expandedIndices[index]);
    }

    private toggleExpandAll(renderedIndices: number[]) {
        const isAllExpanded = this.isAllExpanded(renderedIndices);
        const expandedIndices: { [index: number]: boolean } = {};
        renderedIndices.forEach((index) => { expandedIndices[index] = !isAllExpanded; });
        this.setState({ expandedIndices });
    }

    public render() {
        let self = this;
        if (self.props.data.loading) {
            return (
                <SkeletonLoader variant="panel" columns={1} rows={4} wrapInPaper={false} />
            );
        }

        if (self.props.data.matter && self.props.data.matter.topics ) {
            // tslint:disable-next-line:no-any
            var topics: any[]  = self.props.data.matter.topics.topics;

            const isActionstep = (self.props.sourceSystem || '').toLowerCase() === 'actionstep';
            // tslint:disable-next-line:no-any
            const commentTopics: any[] = isActionstep ? topics.filter((t: any) => t.topic === 'Comments') : [];

            // Track whether we have already inserted the grouped Comments panel
            let commentsGroupRendered = false;

            // Same de-duplicated indices the render loop below actually turns into a panel -
            // computed once up front so the Expand all/Collapse all button (which needs the
            // full set before the loop runs) and the loop itself never disagree about which
            // indices exist. Mirrors the loop's own "already rendered the grouped panel" skip.
            let commentsGroupCounted = false;
            // tslint:disable-next-line:no-any
            const renderedIndices: number[] = topics.reduce((acc: number[], topic: any, index: number) => {
                if (isActionstep && topic.topic === 'Comments') {
                    if (commentsGroupCounted) {
                        return acc;
                    }
                    commentsGroupCounted = true;
                }
                acc.push(index);
                return acc;
            }, []);
            const isAllExpanded = this.isAllExpanded(renderedIndices);

            return (
                <div>
                    <ExpandToggleWrapper>
                        <Button
                            size="small"
                            startIcon={isAllExpanded ? <UnfoldLessOutlinedIcon fontSize="small" /> : <UnfoldMoreOutlinedIcon fontSize="small" />}
                            onClick={() => this.toggleExpandAll(renderedIndices)}
                        >
                            {isAllExpanded ? 'Collapse all' : 'Expand all'}
                        </Button>
                    </ExpandToggleWrapper>
                    {
                        // tslint:disable-next-line:no-any
                        topics.map((topic: any, index: number) => {
                            // Actionstep: collapse all "Comments" topics into one panel
                            if (isActionstep && topic.topic === 'Comments') {
                                if (commentsGroupRendered) {
                                    // Skip — already rendered the grouped panel
                                    return null;
                                }
                                commentsGroupRendered = true;
                                return (
                                    <RelationshipWrapper key="comments-group" className="relationship-wrapper">
                                        <AnimatedItem noFill={true} delay={index * 30}>
                                            <Accordion
                                                expanded={!!this.state.expandedIndices[index]}
                                                onChange={() => this.toggleExpanded(index)}
                                            >
                                                <AccordionSummary expandIcon={<PanelIcon />}>
                                                    <PanelTitle>
                                                        <PanelIconChip bg="var(--rv-accent-purple-bg)" color="var(--rv-accent-purple)">
                                                            <ChatBubbleOutlineIcon fontSize="small" />
                                                        </PanelIconChip>
                                                        {`Comments (${commentTopics.length})`}
                                                    </PanelTitle>
                                                </AccordionSummary>
                                                <PanelDetails className="centered" style={{ flexDirection: 'column' }}>
                                                    {commentTopics.map((comment: any, idx: number) => (
                                                        <CommentCard key={idx}>
                                                            <RvDataTopicInfo
                                                                data={comment.topicData}
                                                                description={comment.description}
                                                            />
                                                        </CommentCard>
                                                    ))}
                                                </PanelDetails>
                                            </Accordion>
                                        </AnimatedItem>
                                    </RelationshipWrapper>
                                );
                            }

                            return (
                                <RelationshipWrapper key={'relationship-' + index} className="relationship-wrapper">
                                    <AnimatedItem noFill={true} delay={index * 30}>
                                        <Accordion
                                            expanded={!!this.state.expandedIndices[index]}
                                            onChange={() => this.toggleExpanded(index)}
                                        >
                                            <AccordionSummary expandIcon={<PanelIcon />}>
                                                <PanelTitle>
                                                    <PanelIconChip bg="var(--rv-accent-grey-bg)" color="var(--rv-accent-grey)">
                                                        <DescriptionOutlinedIcon fontSize="small" />
                                                    </PanelIconChip>
                                                    {topic.topic}
                                                </PanelTitle>
                                            </AccordionSummary>
                                            <PanelDetails className="centered">
                                                <RvDataTopicInfoCard
                                                    key={index.toString()}
                                                    description={topic.description}
                                                    data={topic.topicData}
                                                    title={topic.topic}
                                                />
                                            </PanelDetails>
                                        </Accordion>
                                    </AnimatedItem>
                                </RelationshipWrapper>
                            );
                        })
                    }
                </div>
            );
        }

        return <div />;

    }
}

export const MatterDataTopic  = gql`
query MatterDataTopic ($matterId: Int!) {
    matter(id: $matterId) {
        id,
        topics {
            recordCount
            topics {
                topic
                description
                sortOrder
                topicData {
                    label
                    type
                    dateFormat
                    dateValue
                    stringValue
                    sortOrder
                }
            }
        }
    }
}`;

// tslint:disable-next-line:no-any
export const Topics = graphql<any, TopicsInfoProps, any>
    (MatterDataTopic, {
        // Was inheriting the app's global default (cache-and-network) - always re-fetching on
        // every tab mount regardless of cache. Cached after the first load; the header's
        // Refresh button (MatterDetails.tsx) forces a re-fetch via MATTER_DETAILS_REFRESH_EVENT.
        options: ({ matterId }) => ({ variables: { matterId: matterId }, fetchPolicy: 'cache-first' })
    })
    (TopicsInfo);